// A customer's Email tab (Session AUB, Item 7): the mail received from this customer and the replies ELE sent them,
// DERIVED LIVE on every read (GET /api/mail/customers/:id/email) from the recorded inbound mail matched by the customer's
// recorded addresses and contacts, and from the reply library. Nothing is copied into the customer's Communications
// timeline (RULE 6). Paginated on the server (RULE 5).
//
// Session AUG (email system Phase 6, Item 3, D221): EVERY email ELE sent them, read from Gmail's Sent folder as well (a
// quote cover email, a renewal invitation, a report email, an Outbox notice), each marked as composed by the platform or
// written by a person. All and Sent page by the instant of the last row shown (Older / Newer), since the Sent folder has
// no page numbers; Received pages as before. A Sent read that did not complete is SAID, never shown as "no email".
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import type { CustomerEmailRow, CustomerSentFolderRead } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { formatDateTime } from "../../lib/format";
import { EmptyState } from "../../components/EmptyState";
import { Loader } from "../../components/Loader";
import { StatusPill } from "../../components/StatusPill";

export const CUSTOMER_EMAIL_PAGE_SIZE = 25;

type Direction = "all" | "inbound" | "sent";
const DIRECTIONS: Array<{ value: Direction; label: string }> = [
  { value: "all", label: "All" },
  { value: "inbound", label: "Received" },
  { value: "sent", label: "Sent" },
];

const SMALL_BTN = "rounded-ele border border-ele-purple-accent px-3 py-1.5 font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-strong disabled:opacity-40 max-sm:min-h-11";

/** The words for a sent email's origin. */
export function originLabel(row: CustomerEmailRow): string | null {
  if (row.direction !== "sent" || !row.origin) return null;
  return row.origin === "platform" ? "Platform" : "Person";
}

export function CustomerEmailTab({ customerId }: { customerId: string }) {
  const [direction, setDirection] = useState<Direction>("all");
  // Received: offset paging. All and Sent: the stack of page cursors (cursors[i] is the "before" of page i; page 0 has none).
  const [offset, setOffset] = useState(0);
  const [cursors, setCursors] = useState<Array<string | undefined>>([undefined]);
  const [nextBefore, setNextBefore] = useState<string | null>(null);
  const [rows, setRows] = useState<CustomerEmailRow[]>([]);
  const [total, setTotal] = useState<number | null>(0);
  const [sentFolder, setSentFolder] = useState<CustomerSentFolderRead | null>(null);
  const [addresses, setAddresses] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const cursorMode = direction !== "inbound";
  const before = cursors[cursors.length - 1];

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.mail.customerEmail(customerId, {
        direction: direction === "all" ? undefined : direction,
        limit: CUSTOMER_EMAIL_PAGE_SIZE,
        ...(direction === "inbound" ? { offset } : before ? { before } : {}),
      });
      setRows(res.emails);
      setTotal(typeof res.total === "number" ? res.total : null);
      setNextBefore(res.nextBefore ?? null);
      setSentFolder(res.sentFolder ?? null);
      setAddresses(res.addresses);
    } catch (err) {
      setError(friendlyApiError(err, "Could not load this customer's email."));
    } finally {
      setLoading(false);
    }
  }, [customerId, direction, offset, before]);

  // A Sent read that did not complete (and is not simply "this customer has no address to search").
  const sentProblem = sentFolder && sentFolder.state !== "complete" && sentFolder.state !== "no_addresses" ? sentFolder : null;

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="space-y-3">
      <p className="text-sm text-ele-body">
        The mail received from this customer, matched by their recorded email addresses and contacts, and every email ELE sent
        them, read from the mailbox's Sent folder: quotes, renewal invitations, reports and notices the platform composed, and
        emails a person wrote. Nothing is copied: this is read fresh each time.
      </p>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-2" role="group" aria-label="Show">
          {DIRECTIONS.map((d) => (
            <button
              key={d.value}
              type="button"
              aria-pressed={direction === d.value}
              onClick={() => {
                setDirection(d.value);
                setOffset(0);
                setCursors([undefined]);
              }}
              className={`rounded-ele border px-3 py-1.5 font-menu text-xs tracking-wide transition max-sm:min-h-11 ${
                direction === d.value ? "border-ele-green text-ele-green" : "border-ele-purple-accent text-ele-body hover:text-ele-strong"
              }`}
            >
              {d.label}
            </button>
          ))}
        </div>
        <Link to={`/office/mail?tab=all&customerId=${encodeURIComponent(customerId)}`} className="font-menu text-xs tracking-wide text-ele-blue hover:text-ele-strong max-sm:inline-flex max-sm:min-h-11 max-sm:items-center">
          Open in Mail
        </Link>
      </div>
      {addresses.length > 0 && <p className="text-xs text-ele-body [overflow-wrap:anywhere]">Addresses matched: {addresses.join(", ")}</p>}
      {sentProblem && !error && (
        <p role="status" className="rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-sm text-[var(--ele-amber-text)]" data-testid="customer-email-sent-problem">
          {sentProblem.message ?? "The Sent folder could not be read, so some emails ELE sent may be missing from this list."}
        </p>
      )}

      {loading && rows.length === 0 ? (
        <Loader label="Loading the email..." rows={4} />
      ) : error ? (
        <div role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center">
          <p className="text-ele-strong">{error}</p>
          <button type="button" onClick={() => void load()} className={`mt-3 ${SMALL_BTN}`}>
            Retry
          </button>
        </div>
      ) : rows.length === 0 && sentProblem ? (
        <p className="text-sm text-ele-body" data-testid="customer-email-none-recorded">
          No received mail or recorded reply is listed here, but that is not the whole picture: the Sent folder was not fully read.
        </p>
      ) : rows.length === 0 ? (
        <EmptyState
          message={direction === "sent" ? "No replies to this customer are on record." : direction === "inbound" ? "No mail from this customer is on record." : "No email with this customer is on record."}
          hint={addresses.length === 0 ? "Add the customer's email address, or a contact with one, so their mail can be matched." : undefined}
        />
      ) : (
        <>
          <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
            <table className="ele-list-table w-full text-left text-sm">
              <colgroup>
                <col className="max-md:hidden" style={{ width: "18%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "50%" }} />
                <col style={{ width: "20%" }} />
              </colgroup>
              <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
                <tr>
                  <th className="max-md:hidden px-3 py-2">Date</th>
                  <th className="px-3 py-2">Way</th>
                  <th className="px-3 py-2">Email</th>
                  <th className="px-3 py-2">Gmail</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={`${r.direction}-${r.source ?? "recorded"}-${r.id}`} className="border-t border-ele-purple-accent" data-testid={`customer-email-${r.id}`}>
                    <td className="max-md:hidden truncate px-3 py-2 align-top text-ele-body">{formatDateTime(r.at)}</td>
                    <td className="px-3 py-2 align-top">
                      <StatusPill word={r.direction === "inbound" ? "In" : "Out"} tone={r.direction === "inbound" ? "blue" : "green"} />
                      {originLabel(r) && (
                        <span
                          className="mt-1 block font-menu text-[11px] tracking-wide text-ele-body"
                          title={r.origin === "platform" ? "A branded email the platform composed: a quote, an invitation, a report or a notice" : "An email a person wrote, or a drafted reply a person checked and sent"}
                          data-testid={`customer-email-origin-${r.id}`}
                        >
                          {originLabel(r)}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <div className="truncate text-ele-strong" title={r.subject ?? ""}>
                        {r.withheld ? "Payment details email (withheld)" : r.subject?.trim() || "(no subject)"}
                      </div>
                      <div className="truncate text-xs text-ele-body" title={r.counterpart ?? ""}>
                        {r.direction === "inbound" ? "From " : "To "}
                        {r.counterpart ?? "unknown"}
                        <span className="md:hidden"> · {formatDateTime(r.at)}</span>
                      </div>
                      {r.snippet && <div className="truncate text-xs text-ele-body opacity-80">{r.snippet}</div>}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <a href={r.gmailUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-ele-blue underline-offset-2 hover:underline">
                        Open in Gmail
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {cursorMode ? (
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-ele-body">
              <span>
                Page {cursors.length}, newest first
              </span>
              <div className="flex gap-2">
                <button type="button" disabled={cursors.length <= 1 || loading} onClick={() => setCursors(cursors.slice(0, -1))} className={SMALL_BTN}>
                  Newer
                </button>
                <button type="button" disabled={!nextBefore || loading} onClick={() => nextBefore && setCursors([...cursors, nextBefore])} className={SMALL_BTN}>
                  Older
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-ele-body">
              <span>
                Showing {offset + 1} to {offset + rows.length} of {total ?? rows.length}
              </span>
              <div className="flex gap-2">
                <button type="button" disabled={offset === 0 || loading} onClick={() => setOffset(Math.max(0, offset - CUSTOMER_EMAIL_PAGE_SIZE))} className={SMALL_BTN}>
                  Previous
                </button>
                <button type="button" disabled={offset + CUSTOMER_EMAIL_PAGE_SIZE >= (total ?? 0) || loading} onClick={() => setOffset(offset + CUSTOMER_EMAIL_PAGE_SIZE)} className={SMALL_BTN}>
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
